import _camelCase from 'lodash/camelCase';
import type { List, String } from 'ts-toolbelt';

export type Replace<
  Subject extends string,
  ToReplace extends string,
  Replacement extends string,
  A extends string = ''
> = Subject extends `${infer L}${ToReplace}${infer R}`
  ? Replace<R, ToReplace, `${A}${L}${Replacement}`>
  : `${A}${Subject}`

export type ReplaceMany<
  Subject extends string,
  ToReplace extends string[],
  Replacement extends string
> = List.Length<ToReplace> extends 0
  ? Subject
  : ReplaceMany<
      Replace<Subject, ToReplace[0], Replacement>,
      List.Tail<ToReplace>,
      Replacement
    >

export type RemoveRepetitive<S extends string, ToRemove extends string> = String.Join<List.Filter<String.Split<S, ToRemove>, ''>, ToRemove>

type NormalizeKey<S extends string> = RemoveRepetitive<ReplaceMany<S, ['?', '.', '(', ')', '/', '|', '\\', '-', ' '], '_'>, '_'>;

type _ToCamelCase<S extends string> = S extends `${infer H}_${infer Tail}`
  ? `${H}${Capitalize<_ToCamelCase<Tail>>}`
  : S;

export type ToCamelCase<T extends string> = _ToCamelCase<NormalizeKey<T>> extends ''
  ? never
  : _ToCamelCase<Lowercase<NormalizeKey<T>>>;

export type CamelCasedProps<TMap extends Record<PropertyKey, PropertyKey>, TData> = {
  [
  P in keyof TData as P extends keyof TMap
    ? TMap[P]
    : P extends string
      ? ToCamelCase<P>
      : P
  ]: TData[P]
};

export type UpperCasedProps<TMap extends Record<PropertyKey, PropertyKey>, TData> = {
  [
  P in keyof TData as P extends keyof TMap
    ? TMap[P]
    : P extends string
      ? Uppercase<P>
      : P
  ]: TData[P]
};

type EmptyObject = Record<never, never>;

export type AutoCamelCaseProps<TData> = CamelCasedProps<EmptyObject, TData>;

type _ToLowerCase<S extends string> = S extends `${infer A}`
  ? `${Lowercase<A>}`
  : S;

export type ToLowerCase<T extends string> = _ToLowerCase<NormalizeKey<T>> extends ''
  ? never
  : _ToLowerCase<Lowercase<NormalizeKey<T>>>;

export type LowerCasedProps<TMap extends Record<PropertyKey, PropertyKey>, TData> = {
  [
  P in keyof TData as P extends keyof TMap
    ? TMap[P]
    : P extends string
      ? ToLowerCase<P>
      : P
  ]: TData[P]
};

export type AutoLowerCaseProps<TData> = LowerCasedProps<EmptyObject, TData>;

export type MappedProps<TMap extends Record<PropertyKey, PropertyKey>, TData> = {
  [
  P in keyof TData as P extends keyof TMap
    ? TMap[P]
    : P
  ]: TData[P]
}

const isObjectKey = (key: PropertyKey | unknown): key is PropertyKey =>
  typeof key === 'string' || typeof key === 'number' || typeof key === 'symbol';

// eslint-disable-next-line valid-jsdoc
/**
 * @example ```
 *  map({
 *    f1: 'Value 1',
 *    f2: 'Value 2',
 *    third_field: 'Value 3',
 *  }) // result: { firstField: 'Value 1', secondField: 'Value 2', third_field: 'Value 3' }
 *
 * ```
 */
export const mapFields = <TMap extends Record<string, string>>(mapping: Readonly<TMap>) =>
  <TData extends Record<string, any>>(data: TData) => {
    if (typeof data !== 'object') {
      throw new Error('data passed to mapFields is not an object');
    }
    if (typeof mapping !== 'object' || Array.isArray(mapping)) {
      throw new Error('map passed to mapFields is not an object');
    }
    return Object.entries(data).reduce((acc, [key, value]) => {
      const newKey = (isObjectKey(mapping[key]) ? mapping[key] : key) as keyof MappedProps<TMap, TData>;
      acc[newKey] = value;
      return acc;
    }, {} as MappedProps<TMap, TData>);
  };

export const mapFieldsToUpperCase = <TData>(data: TData) =>
  Object.entries(data as Record<string, unknown>).reduce((result, [key, value]) => {
    const newKey = key.toUpperCase();
    (result as any)[newKey] = value;
    return result;
  }, {} as UpperCasedProps<{}, TData>);

// eslint-disable-next-line valid-jsdoc
/**
 * @example ```
 *  map({
 *    f1: 'Value 1',
 *    f2: 'Value 2',
 *    third_field: 'Value 3'
 *  }) // result: { firstField: 'Value 1', secondField: 'Value 2', thirdField: 'Value 3' }
 *
 * ```
 */
export const autoCamelCaseWithCustomRules = <TMap extends Record<PropertyKey, PropertyKey>>(map: Readonly<TMap>) =>
  <TData>(data: TData): CamelCasedProps<TMap, TData> =>
    Object.entries(data as Record<string, unknown>).reduce((result, [key, value]) => {
      const newKey = isObjectKey(map[key])
        ? map[key]
        : _camelCase(key);
      (result as any)[newKey] = value;
      return result;
    }, {} as CamelCasedProps<TMap, TData>);


export const autoCamelCaseMapping = <TData>(data: TData): AutoCamelCaseProps<TData> =>
  autoCamelCaseWithCustomRules({})(data);
